import {
  AirwallexApplePayButton,
  AirwallexGooglePayButton,
  AirwallexProvider,
  AirwallexSplitCardForm,
} from '@checkout/ui'
import { forwardRef, useImperativeHandle, useRef } from 'react'

import type { CheckoutPresentationRunnerHandle, CheckoutPresentationRunnerProps } from './types'
import type { AirwallexSplitCardFormRef } from '@checkout/ui'

export const AirwallexPresentationRunner = forwardRef<
  CheckoutPresentationRunnerHandle,
  CheckoutPresentationRunnerProps
>(function AirwallexPresentationRunner(
  { execution, method, payer, countryCode, merchantName, onOutcome, onValidityChange },
  ref
) {
  if (!execution) {
    throw new Error('Airwallex checkout execution is required')
  }
  const cardRef = useRef<AirwallexSplitCardFormRef>(null)
  const presentation = execution.presentation

  if (presentation?.flow !== 'airwallex_dropin') {
    throw new Error('Airwallex checkout presentation is required')
  }

  const { payload } = presentation
  const isSubscription = method.renewal_policy !== 'manual'

  useImperativeHandle(
    ref,
    () => ({
      submit: async () => {
        if (method.pm_id !== 'card') {
          throw new Error('Selected Airwallex method owns its submit interaction')
        }
        await cardRef.current?.submit()
      },
    }),
    [method.pm_id]
  )

  const common = {
    intentId: payload.intent_id,
    clientSecret: payload.client_secret,
    currency: execution.currency,
    payer,
    onOutcome,
    isSubscription,
  }

  return (
    <AirwallexProvider env={payload.environment === 'live' ? 'prod' : 'demo'}>
      {method.pm_id === 'card' && (
        <AirwallexSplitCardForm ref={cardRef} {...common} onValidityChange={onValidityChange} />
      )}
      {method.pm_id === 'apple_pay' && (
        <AirwallexApplePayButton
          {...common}
          amount={execution.amount}
          countryCode={countryCode}
          totalPriceLabel={merchantName}
          customerId={payload.customer_id}
        />
      )}
      {method.pm_id === 'google_pay' && (
        <AirwallexGooglePayButton
          {...common}
          amount={execution.amount}
          countryCode={countryCode}
          merchantName={merchantName}
          totalPriceLabel={merchantName}
          customerId={payload.customer_id}
        />
      )}
    </AirwallexProvider>
  )
})
